import React, { useState } from 'react';
import {
  UseFormClearErrors,
  UseFormSetError,
  UseFormSetValue,
  UseFormWatch,
} from 'react-hook-form';
import {
  Autocomplete,
  Box,
  Grid,
  TextField,
  Typography,
  ListSubheader,
} from '@mui/material';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import SearchIcon from '@mui/icons-material/Search';
import parse from 'autosuggest-highlight/parse';
import throttle from 'lodash/throttle';
import { TripLegAddress } from '../../data/models/reservation.model';
import { useTranslation } from 'react-i18next';
import { PassengerAddress } from '../../data/models/passenger.model';
import { useGoogleMaps } from '../../contexts/GoogleMapsProvider';
import isEqual from 'lodash/isEqual';
import uniqBy from 'lodash/uniqBy';

interface MainTextMatchedSubstrings {
  offset: number;
  length: number;
}

interface PlaceType {
  description: string;
  mainText: string;
  secondaryText: string;
  mainTextMatchedSubstrings: readonly MainTextMatchedSubstrings[];
  place_id: string;
  zipCode?: string;
  isHeader?: boolean;
}

interface AddressInputProps {
  label: string;
  name: string;
  required?: boolean;
  errorMessage?: string;
  /** Non-blocking message rendered under the field in the theme warning color. */
  warningMessage?: string;
  watch: UseFormWatch<any>;
  setValue: UseFormSetValue<any>;
  errorHandlers?: {
    setError: UseFormSetError<any>;
    clearErrors: UseFormClearErrors<any>;
  };
  disabled?: boolean;
  passengerAddresses?: PassengerAddress[];
}

// Do not include places without addresses in the autocomplete results
// (e.g neighborhoods, cities, counties, states, countries, etc)
const excludedPlaceTypes = [
  'locality',
  'sublocality',
  'sublocality_level_1',
  'sublocality_level_2',
  'sublocality_level_3',
  'sublocality_level_4',
  'sublocality_level_5',
  'neighborhood',
  'continent',
  'country',
  'administrative_area_level_1',
  'administrative_area_level_2',
  'administrative_area_level_3',
  'administrative_area_level_4',
  'administrative_area_level_5',
  'administrative_area_level_6',
  'administrative_area_level_7',
];

export default function AddressInput({
  label,
  name,
  required = false,
  errorMessage,
  warningMessage,
  watch,
  setValue,
  errorHandlers,
  disabled = false,
  passengerAddresses = [],
}: AddressInputProps) {
  const { t } = useTranslation('common');
  const { places } = useGoogleMaps();

  const [selectedValue, setSelectedValue] = React.useState<PlaceType | null>(
    null,
  );
  const [inputValue, setInputValue] = React.useState('');
  const [options, setOptions] = React.useState<readonly PlaceType[]>([]);
  const [isAddressValid, setIsAddressValid] = useState(true);

  const currentValue = watch(name);

  const fetch = React.useMemo(
    () =>
      throttle(
        async (
          request: { input: string },
          callback: (results?: readonly PlaceType[]) => void,
        ) => {
          let sessionToken = new places!.AutocompleteSessionToken();

          // Define request options.
          let req = {
            input: request.input,
            sessionToken,
          };
          const { suggestions } = await (
            places! as any
          ).AutocompleteSuggestion.fetchAutocompleteSuggestions(req);
          const filteredSuggestions = suggestions.filter(
            (suggestion: any) =>
              !excludedPlaceTypes.some((type) =>
                suggestion.placePrediction.types.includes(type),
              ),
          );
          callback(filteredSuggestions);
        },
        200,
      ),
    [],
  );

  React.useEffect(() => {
    let active = true;

    const generatePassengerOptions = (): PlaceType[] =>
      passengerAddresses
        ?.map((addr) => ({
          description: `${addr.label} - ${addr.addressLine1}, ${addr.city}`,
          place_id: addr.placeID as string,
          mainText: `${addr.addressLine1}`,
          secondaryText: `${addr.city}, ${addr.stateCode} ${addr.zipCode}`,
          mainTextMatchedSubstrings: [],
          zipCode: addr.zipCode,
        }))
        .filter((option) =>
          option.description.toLowerCase().includes(inputValue.toLowerCase()),
        ) || [];

    const updateOptions = (results: PlaceType[] = []) => {
      const passengerOptions: PlaceType[] = generatePassengerOptions();

      const combinedOptions: PlaceType[] = [
        ...(passengerOptions.length > 0
          ? [
              {
                description: 'Recent Addresses',
                place_id: 'header',
                isHeader: true,
              } as PlaceType,
            ]
          : []),
        ...passengerOptions,
        ...results,
      ];

      const areOptionsEqual = isEqual(options, combinedOptions);
      if (!areOptionsEqual) {
        setOptions(combinedOptions);
      }
    };

    if (inputValue.length > 0) {
      fetch({ input: inputValue }, (results?: readonly any[]) => {
        const suggestions: PlaceType[] =
          results?.map((r) => {
            const p = (r as any).placePrediction;
            return {
              description: p.text.text,
              mainText: p.mainText.text,
              secondaryText: p.secondaryText?.text,
              mainTextMatchedSubstrings: p.mainText.matches.map((m: any) => {
                const start = m.startOffset;
                const end = m.endOffset;
                return { length: end - start, offset: start };
              }),
              place_id: p.placeId,
              isHeader: false,
            };
          }) || [];
        if (active) {
          updateOptions(suggestions ? [...suggestions] : []);
        }
      });
    } else if (passengerAddresses && passengerAddresses.length > 0) {
      updateOptions();
    } else {
      return () => {
        active = false;
      };
    }
  }, [selectedValue, inputValue, fetch, passengerAddresses]);

  React.useEffect(() => {
    setSelectedValue(currentValue || null);
  }, [currentValue]);

  const getAddrComponent = (
    addrComponents: any[],
    name: string,
    short: boolean = false,
  ): string => {
    const comp = addrComponents.find((c) =>
      (c.types as string[]).includes(name),
    );
    return comp ? (short ? comp.shortText : comp.longText) : '';
  };

  const convertAddress = (addrComponents: any[]): TripLegAddress => {
    const streetNumber = getAddrComponent(addrComponents, 'street_number');
    const sub = getAddrComponent(addrComponents, 'subpremise') || undefined;

    return {
      addressLine1: `${streetNumber} ${getAddrComponent(addrComponents, 'route')}`,
      addressLine2: sub,
      city:
        getAddrComponent(addrComponents, 'locality') ||
        getAddrComponent(addrComponents, 'political'),
      stateCode: getAddrComponent(
        addrComponents,
        'administrative_area_level_1',
        true,
      ),
      county: getAddrComponent(
        addrComponents,
        'administrative_area_level_2',
        true,
      ),
      zipCode: getAddrComponent(addrComponents, 'postal_code'),
    };
  };

  const handleChange = async (_evt: any, newValue: PlaceType | null) => {
    // Show the selection immediately (display is driven by `selectedValue`), but
    // defer writing the form field until the zip is resolved. Writing once — with
    // the complete address — means a single FIELD_EDITED, so the bundled
    // validation fires /score/trip-leg only once per pick instead of twice
    // (zip-less, then enriched).
    setSelectedValue(newValue);

    if (newValue?.place_id && !newValue.place_id.startsWith('passenger-')) {
      try {
        const placeId = newValue.place_id;
        const place = new places!.Place({
          id: placeId,
          requestedLanguage: 'en',
        });
        await place.fetchFields({
          fields: ['addressComponents', 'location', 'formattedAddress'],
        });
        const address = convertAddress(place.addressComponents!);
        if (!address?.addressLine1?.trim()) {
          setIsAddressValid(false);
          errorHandlers?.setError(name, {
            type: 'validate',
            message: t('common.invalidAddress'),
          });
        } else {
          setIsAddressValid(true);
          errorHandlers?.clearErrors(name);
        }
        address.latitude = place.location!.lat();
        address.longitude = place.location!.lng();
        const enriched = { ...newValue, zipCode: address.zipCode };
        setValue(`${name}Struct`, JSON.stringify(address), {
          shouldDirty: true,
        });
        setValue(name, enriched, { shouldDirty: true });
        setSelectedValue(enriched);
      } catch (_err) {
        // Place Details failed (e.g. a saved passenger address carrying a stale
        // place id → GetPlace 404). Keep the selection but drop its place_id so
        // validation's resolveMissingPlaceIds re-resolves it from the display
        // text and fills `*AddressStruct`. See: F1V2-6685.
        const { place_id: _stalePlaceId, ...withoutPlaceId } = newValue;
        setValue(`${name}Struct`, '', { shouldDirty: true });
        setValue(name, withoutPlaceId, { shouldDirty: true });
      }
    } else {
      // Pre-configured (passenger) addresses already carry the zip, and clearing
      // the field has no details to fetch — write immediately. A clear writes ''
      // (never null): the address schema's empty representation is the empty
      // string, and null fails its string/place type union on the next resolver
      // run with a raw Zod message.
      setValue(name, newValue ?? '', { shouldDirty: true });
      setValue(`${name}Struct`, '', { shouldDirty: true });
    }
  };

  const handleInputChange = (_evt: any, newInputValue: string) => {
    setInputValue(newInputValue);
  };

  const formatLabel = (option: PlaceType) => {
    if (option.isHeader) {
      return option.description;
    }
    if (option?.mainText) {
      const street = option?.mainText;
      let city = option.secondaryText || '';
      const zip = option.zipCode;
      if (zip) {
        city = city.replace(` ${zip}`, '');
      }
      return `${street} ${city} ${option.zipCode || ''}`;
    }
    return `${option.description} ${option.zipCode}`;
  };

  if (!isAddressValid) {
    errorMessage = t('common.invalidAddress');
  }

  const autocompleteOptions =
    selectedValue && selectedValue.place_id
      ? uniqBy([selectedValue, ...options], 'place_id')
      : options;

  return (
    <Autocomplete
      getOptionLabel={(option) => formatLabel(option)}
      isOptionEqualToValue={(opt, val) =>
        (opt.place_id ?? opt.description) === (val.place_id ?? val.description)
      }
      filterOptions={(x) => x}
      options={autocompleteOptions}
      autoComplete
      includeInputInList
      filterSelectedOptions
      value={selectedValue}
      noOptionsText={
        inputValue ? t('common.noLocations') : t('common.typeToSearch')
      }
      popupIcon={<SearchIcon className="transform-none" />}
      onChange={handleChange}
      onInputChange={handleInputChange}
      renderInput={(params) => {
        const hasError = !isAddressValid || !!errorMessage?.length;
        const helperText =
          errorMessage || warningMessage ? (
            <>
              {errorMessage ? (
                <Box
                  component="span"
                  sx={{ display: 'block' }}
                  data-testid="address-error">
                  {errorMessage}
                </Box>
              ) : null}
              {warningMessage ? (
                <Box
                  component="span"
                  sx={{ display: 'block', color: 'warning.main' }}>
                  {warningMessage}
                </Box>
              ) : null}
            </>
          ) : undefined;
        return (
          <TextField
            {...params}
            label={label}
            required={required}
            variant="outlined"
            type="text"
            error={hasError}
            helperText={helperText}
          />
        );
      }}
      renderOption={(props, option) => {
        if (option.isHeader) {
          return (
            <ListSubheader key={option.place_id} component="div">
              {option.description}
            </ListSubheader>
          );
        }

        const matches = option.mainTextMatchedSubstrings || [];
        const parts = parse(
          option.mainText || '',
          matches.map((match: any) => [
            match.offset,
            match.offset + match.length,
          ]),
        );

        return (
          <li {...props} key={option.place_id}>
            <Grid container alignItems="center">
              <Grid item sx={{ display: 'flex', width: 44 }}>
                <LocationOnIcon sx={{ color: 'text.secondary' }} />
              </Grid>
              <Grid item xs>
                {parts.map((part, index) => (
                  <Box
                    key={index}
                    component="span"
                    sx={{
                      fontWeight: part.highlight ? 'bold' : 'regular',
                    }}>
                    {part.text}
                  </Box>
                ))}
                <Typography variant="body2" color="text.secondary">
                  {option.secondaryText}
                </Typography>
              </Grid>
            </Grid>
          </li>
        );
      }}
    />
  );
}
